import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Collapse,
  Divider,
  Grid,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArchitectureIcon from "@mui/icons-material/Architecture";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useDropzone } from "react-dropzone";
import api from "../../utils/api";

const steps = ["Request Info", "Details & Files", "Review & Submit"];

const AddArchitecturalRequestSmart = ({ propertyId, accountId, onClose }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [requestTypes, setRequestTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [showContractor, setShowContractor] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [colors, setColors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [schemes, setSchemes] = useState([]);
  const [filteredSchemes, setFilteredSchemes] = useState([]);
  const [multiSubtypeSelections, setMultiSubtypeSelections] = useState([]);
  const [variantColors, setVariantColors] = useState([]);
  const [showVariantTable, setShowVariantTable] = useState(false);
  const [variantSearch, setVariantSearch] = useState("");
  const [page, setPage] = useState(1);
  const [typeHasColors, setTypeHasColors] = useState(false);
  const [variantSelectedColor, setVariantSelectedColor] = useState(null);

  const pageSize = 10;

  const [contractor, setContractor] = useState({
    contractor_name: "",
    phone_number: "",
    company_name: "",
    notes: "",
  });

  const [formData, setFormData] = useState(() => {
    const draft = localStorage.getItem("arch_request_draft");
    return draft
      ? JSON.parse(draft)
      : {
          description: "",
          request_type: "",
          subtype_id: "",
          color_id: "",
          property_id: propertyId || "",
          account_id: accountId || "",
          comment: "",
          acknowledged: false,
          anticipated_start_date: "",
          anticipated_completion_date: "",
          freeform_content: "",
        };
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");
    let parsedUserId = null;

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        parsedUserId = parsedUser?.userId ?? null;
      } catch (e) {
        console.error("Failed to parse user from sessionStorage", e);
      }
    }

    setCurrentUserId(parsedUserId);
  }, []);

  useEffect(() => {
    localStorage.setItem("arch_request_draft", JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      property_id: propertyId || "",
      account_id: accountId || "",
    }));
  }, [propertyId, accountId]);

  useEffect(() => {
    const fetchTypesAndSchemes = async () => {
      try {
        const [typesRes, schemesRes] = await Promise.all([
          api.get(`/architectural-request-types`),
          api.get(`/color-management`),
        ]);
        setRequestTypes(typesRes.data || []);
        setSchemes(schemesRes.data || []);
      } catch (err) {
        console.error("Failed to fetch data:", err);
      }
    };
    fetchTypesAndSchemes();
  }, []);

  const handleFormChange = async (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "request_type") {
      const typeId = Number(value);
      if (!typeId) return;

      try {
        const subtypeRes = await api.get(`/architectural-request-subtypes`, {
          params: { typeId },
        });
        setSubtypes(subtypeRes.data || []);

        const schemeRes = await api.get(`/color-options/by-type`, {
          params: { type_id: typeId },
        });

        const hasColorSchemes = schemeRes.data && schemeRes.data.length > 0;
        setTypeHasColors(hasColorSchemes);

        setFormData((prev) => ({
          ...prev,
          request_type: typeId,
          subtype_id: "",
          color_id: "",
          scheme_id: "",
        }));

        setFilteredSchemes(schemeRes.data || []);
        setColors([]);
      } catch (err) {
        console.error("Error fetching subtypes/schemes:", err);
        setSubtypes([]);
        setFilteredSchemes([]);
        setColors([]);
        setTypeHasColors(false);
      }
    }

    if (name === "scheme_id") {
      const schemeId = value && !isNaN(value) ? Number(value) : null;
      if (!schemeId) return;

      setFormData((prev) => ({
        ...prev,
        scheme_id: schemeId,
        color_id: "",
      }));

      setColors([]);
    }

    if (name === "subtype_id") {
      const selectedSubtypeId = Number(value);
      setFormData((prev) => ({ ...prev, subtype_id: selectedSubtypeId }));

      setColors([]);
      setMultiSubtypeSelections([]);
      setVariantColors([]);
      setShowVariantTable(null);

      const hasType = name === "request_type" ? Number(value) : formData.request_type;
      const hasScheme =
        name === "scheme_id" ? Number(value) : formData.scheme_id;

      if (typeHasColors && (!hasType || !hasScheme)) {
        setSnackbar({
          open: true,
          message: "Please select a Request Type and Scheme first.",
          severity: "warning",
        });
        return;
      }

      if (selectedSubtypeId === 1057) {
        try {
          const subtypeRes = await api.get(`/architectural-request-subtypes`, {
            params: { typeId: formData.request_type },
          });

          const allSubtypes = subtypeRes.data?.filter(
            (s) => s.subtype_id !== 1057
          );
          const colorData = [];

          for (const sub of allSubtypes) {
            const colorRes = await api.get(`/color-options/by-scheme`, {
              params: {
                scheme_id: Number(formData.scheme_id),
                type_id: Number(formData.request_type),
                subtype_id: sub.subtype_id,
              },
            });

            let subtypeColors = colorRes.data || [];

            if (sub.subtype_id === 1061) {
              subtypeColors = [
                ...subtypeColors,
                { id: "variant", color_name: "Variant (All Colors)" },
              ];
            }

            colorData.push({
              subtype_id: sub.subtype_id,
              subtype_name: sub.subtype_name,
              colors: subtypeColors,
              selectedColor: "",
            });
          }

          setMultiSubtypeSelections(colorData);
        } catch (err) {
          console.error("Error loading multiple subtype colors:", err);
        }
        return;
      }

      if (selectedSubtypeId === 1061) {
        try {
          const colorRes = await api.get(`/color-options/by-scheme`, {
            params: {
              scheme_id: Number(formData.scheme_id),
              type_id: Number(formData.request_type),
              subtype_id: selectedSubtypeId,
            },
          });

          const colorData = [
            ...(colorRes.data || []),
            { id: "variant", color_name: "Variant (All Colors)" },
          ];

          setColors(colorData);
        } catch (err) {
          console.error("Error loading Front Door & Shutters colors:", err);
          setColors([]);
        }
        return;
      }

      try {
        const colorRes = await api.get(`/color-options/by-scheme`, {
          params: {
            scheme_id: Number(formData.scheme_id),
            type_id: Number(formData.request_type),
            subtype_id: selectedSubtypeId,
          },
        });
        setColors(colorRes.data || []);
      } catch (err) {
        console.error("Error fetching colors for subtype:", err);
        setColors([]);
      }
    }
  };

  const handleContractorChange = (e) => {
    const { name, value } = e.target;
    setContractor((prev) => ({ ...prev, [name]: value }));
  };

  const onDrop = useCallback((acceptedFiles) => {
    setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const removeFile = (i) => {
    setSelectedFiles((prev) => prev.filter((_, index) => index !== i));
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [],
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
        ".docx",
      ],
    },
    multiple: true,
  });

  const nextStep = () => setActiveStep((s) => s + 1);
  const prevStep = () => setActiveStep((s) => s - 1);

  const handleSubmit = async () => {
    if (!formData.request_type) {
      setSnackbar({
        open: true,
        message: "Please select a Request Type before submitting.",
        severity: "warning",
      });
      setActiveStep(0);
      return;
    }

    if (selectedFiles.length === 0) {
      setSnackbar({
        open: true,
        message: "At least one file (photo or document) is required.",
        severity: "warning",
      });
      setActiveStep(1);
      return;
    }

    if (!acknowledged) {
      setSnackbar({
        open: true,
        message: "Please acknowledge the certification before submitting.",
        severity: "warning",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const selectedSubtype = subtypes.find(
        (s) => s.subtype_id === Number(formData.subtype_id)
      );

      const res = await api.post(`/architectural-requests`, {
        ...formData,
        acknowledged: true,
        created_by: currentUserId,
      });

      const request_id = res.data?.id;
      if (!request_id) throw new Error("Missing request_id from response");

      if (formData.scheme_id) {
        try {
          if (formData.subtype_id === 1057 && multiSubtypeSelections.length > 0) {
            for (const item of multiSubtypeSelections) {
              if (!item.selectedColor) continue;

              let colorOptionId = item.selectedColor;
              if (item.subtype_id === 1061 && colorOptionId === "variant") {
                continue;
              }

              await api.post(`/architectural-request-color-selections`, {
                architectural_request_id: request_id,
                scheme_id: formData.scheme_id,
                color_option_id: colorOptionId,
                type_id: formData.request_type,
                subtype_id: item.subtype_id,
              });
            }
          } else if (formData.color_id) {
            await api.post(`/architectural-request-color-selections`, {
              architectural_request_id: request_id,
              scheme_id: formData.scheme_id,
              color_option_id: formData.color_id,
              type_id: formData.request_type,
              subtype_id: formData.subtype_id,
            });
          }
        } catch (colorErr) {
          console.warn("Failed to record color selection(s):", colorErr);
        }
      }

      if (selectedFiles.length > 0) {
        const uploadData = new FormData();
        uploadData.append("request_id", request_id);
        selectedFiles.forEach((file) => uploadData.append("upload_urls", file));

        await api.post(`/request-files`, uploadData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      if (showContractor && contractor.contractor_name.trim()) {
        await api.post(`/architectural-request-contractors`, {
          architectural_request_id: request_id,
          ...contractor,
        });
      }

      if (selectedSubtype?.requires_freeform && formData.freeform_content) {
        await api.post(`/architectural-request-freeform-responses`, {
          request_id,
          type_id: formData.request_type,
          subtype_id: formData.subtype_id,
          field_content: formData.freeform_content,
        });
      }

      setFormData({
        description: "",
        request_type: "",
        subtype_id: "",
        scheme_id: "",
        color_id: "",
        property_id: propertyId,
        account_id: accountId,
        comment: "",
        acknowledged: false,
        anticipated_start_date: "",
        anticipated_completion_date: "",
        freeform_content: "",
      });

      setContractor({
        contractor_name: "",
        phone_number: "",
        company_name: "",
        notes: "",
      });

      setSelectedFiles([]);
      setShowContractor(false);
      setMultiSubtypeSelections([]);
      setVariantColors([]);
      setFilteredSchemes([]);
      setColors([]);
      setShowVariantTable(false);
      setVariantSearch("");
      setPage(1);
      setAcknowledged(false);
      setActiveStep(0);
      setTypeHasColors(false);
      setVariantSelectedColor(null);
      localStorage.removeItem("arch_request_draft");

      setSnackbar({
        open: true,
        message: "✅ Architectural Request submitted successfully!",
        severity: "success",
      });

      setTimeout(() => {
        if (onClose) onClose();
      }, 2000);
    } catch (err) {
      console.error("Submit Error:", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Submission failed",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const sectionCardSx = {
    ...pageCardSx,
    p: { xs: 1.5, sm: 2, md: 2.5 },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const readonlyFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f7f9fc",
      borderRadius: "10px",
    },
  };

  const selectSx = {
    borderRadius: "10px",
    backgroundColor: "rgba(255,255,255,0.96)",
    "& .MuiSelect-select": {
      py: 1.7,
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.5,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2.25,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const renderVariantTable = (onSelectColor, selectedId) => {
    if (!variantColors.length) return null;

    const filtered = variantColors.filter((color) =>
      color.color_name.toLowerCase().includes(variantSearch.toLowerCase())
    );
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    const paged = filtered.slice(start, end);
    const totalPages = Math.ceil(filtered.length / pageSize);

    return (
      <Box sx={{ mt: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Search colors..."
          value={variantSearch}
          onChange={(e) => {
            setVariantSearch(e.target.value);
            setPage(1);
          }}
          sx={{ ...textFieldSx, mb: 2 }}
        />

        <Box
          component="table"
          sx={{
            width: "100%",
            borderCollapse: "collapse",
            backgroundColor: "#fafafa",
            borderRadius: "14px",
            overflow: "hidden",
            "& th, & td": {
              border: "1px solid #e7ebf0",
              padding: "10px 12px",
              textAlign: "left",
            },
            "& th": {
              backgroundColor: "#f7f9fc",
              fontWeight: 700,
              color: "#223142",
            },
            "& tr:hover": {
              backgroundColor: "#eef5ff",
              cursor: "pointer",
            },
          }}
        >
          <thead>
            <tr>
              <th>Color Name</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {paged.length > 0 ? (
              paged.map((color) => (
                <tr
                  key={color.id}
                  onClick={() => onSelectColor(color)}
                  style={{
                    backgroundColor:
                      selectedId === color.id ? "#e8f4ff" : "transparent",
                  }}
                >
                  <td>{color.color_name}</td>
                  <td>{color.description || "-"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={2}
                  style={{
                    textAlign: "center",
                    padding: "16px",
                    color: "#777",
                  }}
                >
                  No colors found.
                </td>
              </tr>
            )}
          </tbody>
        </Box>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={2}
        >
          <Button
            size="small"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            sx={secondaryButtonSx}
          >
            Previous
          </Button>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Page {page} of {totalPages || 1}
          </Typography>
          <Button
            size="small"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            sx={secondaryButtonSx}
          >
            Next
          </Button>
        </Box>
      </Box>
    );
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Card sx={sectionCardSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
              Request Information
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mb: 2.5 }}>
              Select the request type, subtype, and any required color or scheme details.
            </Typography>

            <Box
              sx={{
                p: 2,
                mb: 2.5,
                borderRadius: "14px",
                backgroundColor: "rgba(47,99,173,0.03)",
                border: "1px solid rgba(47,99,173,0.08)",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142" }}>
                Instructions for Applicants
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: "#6b778c" }}>
                Applications must include color photos of the front of the property and the
                proposed impact area. Structural changes require a site plan drawn on the
                official plat map or property survey with measurements. Landscaping changes
                must include a scaled drawing and list of plant materials. Projects near
                easements must include any required permits or approvals. Applications are
                reviewed by Association Management for completeness within one week and must
                be received by the Wednesday before the next scheduled DRB meeting to be
                considered.
              </Typography>
            </Box>

            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Request Type</InputLabel>
                  <Select
                    name="request_type"
                    value={formData.request_type}
                    onChange={handleFormChange}
                    sx={selectSx}
                  >
                    {requestTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Property ID"
                  value={propertyId || "N/A"}
                  InputProps={{ readOnly: true }}
                  sx={readonlyFieldSx}
                />
              </Grid>

              {filteredSchemes.length > 0 && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Scheme</InputLabel>
                    <Select
                      name="scheme_id"
                      value={formData.scheme_id || ""}
                      onChange={handleFormChange}
                      sx={selectSx}
                    >
                      {filteredSchemes.map((scheme) => (
                        <MenuItem key={scheme.scheme_id} value={Number(scheme.scheme_id)}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: "4px",
                                backgroundColor: scheme.hex_value || "#ccc",
                                border: "1px solid #aaa",
                              }}
                            />
                            <Typography>{scheme.color_name}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControl fullWidth disabled={!subtypes.length}>
                  <InputLabel>Subtype</InputLabel>
                  <Select
                    name="subtype_id"
                    value={formData.subtype_id}
                    onChange={handleFormChange}
                    sx={selectSx}
                  >
                    {subtypes.map((sub) => (
                      <MenuItem key={sub.subtype_id} value={sub.subtype_id}>
                        {sub.subtype_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {(() => {
                const selectedSubtype = subtypes.find(
                  (s) => s.subtype_id === Number(formData.subtype_id)
                );

                if (selectedSubtype?.requires_freeform) {
                  return (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Additional Details"
                        name="freeform_content"
                        value={formData.freeform_content || ""}
                        onChange={handleFormChange}
                        multiline
                        rows={3}
                        sx={textFieldSx}
                        placeholder="Enter any additional details required for this subtype..."
                      />
                    </Grid>
                  );
                }
                return null;
              })()}

              {colors.length > 0 && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Color</InputLabel>
                    <Select
                      name="color_id"
                      value={formData.color_id || ""}
                      onChange={async (e) => {
                        const value = e.target.value;
                        setFormData((prev) => ({ ...prev, color_id: value }));

                        if (formData.subtype_id === 1061 && value === "variant") {
                          try {
                            const allColorRes = await api.get(`/color-options`);
                            setVariantColors(allColorRes.data || []);
                            setShowVariantTable(true);
                          } catch (err) {
                            console.error("Failed to fetch all color options:", err);
                            setVariantColors([]);
                            setShowVariantTable(false);
                          }
                        } else {
                          setShowVariantTable(false);
                        }
                      }}
                      renderValue={(selected) => {
                        if (!selected) return <Typography>Select Color</Typography>;

                        const color = colors.find((c) => c.id === selected);

                        if (selected === "variant" && formData.variantSelectedColor) {
                          const chosenVariant = variantColors.find(
                            (v) => v.id === formData.variantSelectedColor
                          );
                          if (chosenVariant) {
                            return (
                              <Box display="flex" alignItems="center" gap={1}>
                                <Box
                                  sx={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: "4px",
                                    backgroundColor: chosenVariant.hex_value || "#ccc",
                                    border: "1px solid #bbb",
                                  }}
                                />
                                <Typography>{`Variant (${chosenVariant.color_name})`}</Typography>
                              </Box>
                            );
                          }
                        }

                        if (selected === "variant") {
                          return (
                            <Typography sx={{ fontStyle: "italic" }}>
                              Variant (All Colors)
                            </Typography>
                          );
                        }

                        return color ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: "4px",
                                backgroundColor: color.hex_value || "#ccc",
                                border: "1px solid #bbb",
                              }}
                            />
                            <Typography>{color.color_name}</Typography>
                          </Box>
                        ) : (
                          <Typography>Select Color</Typography>
                        );
                      }}
                      sx={selectSx}
                    >
                      {colors.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 22,
                                height: 22,
                                borderRadius: "4px",
                                backgroundColor: c.hex_value || "#ccc",
                                border: "1px solid #aaa",
                              }}
                            />
                            <Typography fontSize="0.9rem" fontWeight="500">
                              {c.color_name}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>

                    {showVariantTable && variantColors.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700, color: "#223142" }}>
                          Select a Variant Color from All Schemes
                        </Typography>

                        {renderVariantTable(
                          (color) => {
                            setColors((prev) => {
                              const exists = prev.some((c) => c.id === color.id);
                              if (exists) return prev;
                              return [
                                ...prev,
                                {
                                  id: color.id,
                                  color_name: color.color_name,
                                  description: color.description,
                                },
                              ];
                            });

                            setFormData((prev) => ({
                              ...prev,
                              color_id: color.id,
                              variantSelectedColor: color.id,
                            }));
                            setVariantSelectedColor(color.id);
                          },
                          formData.color_id
                        )}
                      </Box>
                    )}
                  </FormControl>
                </Grid>
              )}

              {multiSubtypeSelections.map((item, i) => (
                <Grid item xs={12} key={i}>
                  <FormControl fullWidth>
                    <InputLabel>{item.subtype_name}</InputLabel>
                    <Select
                      value={item.selectedColor || ""}
                      onChange={async (e) => {
                        const value = e.target.value;
                        const updated = [...multiSubtypeSelections];
                        updated[i].selectedColor = value;
                        setMultiSubtypeSelections(updated);

                        if (item.subtype_id === 1061 && value === "variant") {
                          try {
                            const allColorRes = await api.get(`/color-options`);
                            setVariantColors(allColorRes.data || []);
                            setShowVariantTable(i);
                          } catch (err) {
                            console.error("Failed to load all variant colors:", err);
                            setVariantColors([]);
                            setShowVariantTable(null);
                          }
                        } else {
                          setShowVariantTable(null);
                        }
                      }}
                      renderValue={(selected) => {
                        if (!selected) return <Typography>Select Color</Typography>;

                        const color = item.colors.find((c) => c.id === selected);

                        return color ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: "4px",
                                backgroundColor: color.hex_value || "#ccc",
                                border: "1px solid #bbb",
                              }}
                            />
                            <Typography>{color.color_name}</Typography>
                          </Box>
                        ) : (
                          <Typography>Select Color</Typography>
                        );
                      }}
                      sx={selectSx}
                    >
                      {item.colors.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: "4px",
                                backgroundColor: c.hex_value || "#ccc",
                                border: "1px solid #aaa",
                              }}
                            />
                            <Typography>{c.color_name}</Typography>
                          </Box>
                        </MenuItem>
                      ))}

                      {showVariantTable === i &&
                        variantColors.map((color) => {
                          const exists = item.colors.some((c) => c.id === color.id);
                          if (!exists && item.selectedColor === color.id) {
                            return (
                              <MenuItem key={color.id} value={color.id}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box
                                    sx={{
                                      width: 20,
                                      height: 20,
                                      borderRadius: "4px",
                                      backgroundColor: color.hex_value || "#ccc",
                                      border: "1px solid #aaa",
                                    }}
                                  />
                                  <Typography>{color.color_name}</Typography>
                                </Box>
                              </MenuItem>
                            );
                          }
                          return null;
                        })}
                    </Select>
                  </FormControl>

                  {showVariantTable === i && variantColors.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700, color: "#223142" }}>
                        Select a Variant Color from All Schemes
                      </Typography>

                      {renderVariantTable(
                        (color) => {
                          const updated = [...multiSubtypeSelections];
                          updated[i].selectedColor = color.id;
                          setMultiSubtypeSelections(updated);
                          setShowVariantTable(false);
                        },
                        item.selectedColor
                      )}
                    </Box>
                  )}
                </Grid>
              ))}
            </Grid>
          </Card>
        );

      case 1:
        return (
          <Card sx={sectionCardSx}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
              Details & Files
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mb: 2.5 }}>
              Add request details, timeline, supporting files, and optional contractor information.
            </Typography>

            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <Tooltip title="Provide a detailed explanation of your request">
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    multiline
                    rows={3}
                    sx={textFieldSx}
                    required
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Comment"
                  name="comment"
                  value={formData.comment}
                  onChange={handleFormChange}
                  multiline
                  rows={2}
                  sx={textFieldSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Anticipated Start Date"
                  name="anticipated_start_date"
                  InputLabelProps={{ shrink: true }}
                  value={formData.anticipated_start_date || ""}
                  onChange={handleFormChange}
                  sx={textFieldSx}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Anticipated Completion Date"
                  name="anticipated_completion_date"
                  InputLabelProps={{ shrink: true }}
                  value={formData.anticipated_completion_date || ""}
                  onChange={handleFormChange}
                  sx={textFieldSx}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" sx={{ mb: 1, color: "#6b778c" }}>
                  Attach Supporting Files
                </Typography>

                <Box
                  {...getRootProps()}
                  sx={{
                    p: 3,
                    border: "2px dashed rgba(47,99,173,0.25)",
                    borderRadius: "16px",
                    textAlign: "center",
                    cursor: "pointer",
                    backgroundColor: isDragActive
                      ? "rgba(47,99,173,0.08)"
                      : "rgba(47,99,173,0.02)",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: "#f5faff",
                      borderColor: "#1976d2",
                    },
                    mb: 2,
                  }}
                >
                  <input {...getInputProps()} />
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <CloudUploadIcon sx={{ fontSize: 40, color: "#1976d2", mb: 1 }} />
                    <Typography variant="body2" sx={{ color: "#223142", fontWeight: 600 }}>
                      {isDragActive
                        ? "Drop files here..."
                        : "Drag & drop multiple files here, or click to select"}
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 0.5, color: "#8a94a6" }}>
                      Images, PDFs, or Word documents — at least one required
                    </Typography>
                  </Box>
                </Box>

                {selectedFiles.length > 0 ? (
                  <Box
                    sx={{
                      border: "1px solid rgba(47,99,173,0.08)",
                      borderRadius: "14px",
                      p: 1,
                      maxHeight: 150,
                      overflowY: "auto",
                      backgroundColor: "#fff",
                    }}
                  >
                    {selectedFiles.map((file, idx) => (
                      <Box
                        key={idx}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          borderBottom:
                            idx !== selectedFiles.length - 1
                              ? "1px solid rgba(47,99,173,0.08)"
                              : "none",
                          py: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: "80%",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            color: "#223142",
                          }}
                        >
                          {file.name}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => removeFile(idx)}
                          sx={{
                            border: "1px solid rgba(220,38,38,0.12)",
                            borderRadius: "10px",
                            color: "#dc2626",
                            backgroundColor: "rgba(220,38,38,0.04)",
                            "&:hover": {
                              backgroundColor: "rgba(220,38,38,0.1)",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: "#8a94a6" }}>
                    No files uploaded yet.
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Button
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={() => setShowContractor((prev) => !prev)}
                  variant="outlined"
                  sx={secondaryButtonSx}
                >
                  {showContractor ? "Hide Contractor Info" : "Add Contractor Info"}
                </Button>
              </Grid>

              <Grid item xs={12}>
                <Collapse in={showContractor}>
                  <Box
                    sx={{
                      p: 2,
                      border: "1px solid rgba(47,99,173,0.08)",
                      borderRadius: "14px",
                      backgroundColor: "rgba(47,99,173,0.03)",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ fontWeight: 700, color: "#223142", mb: 1.5 }}
                    >
                      Contractor Information
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          name="contractor_name"
                          label="Name"
                          value={contractor.contractor_name}
                          onChange={handleContractorChange}
                          sx={textFieldSx}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          name="phone_number"
                          label="Phone"
                          value={contractor.phone_number}
                          onChange={handleContractorChange}
                          sx={textFieldSx}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="company_name"
                          label="Company"
                          value={contractor.company_name}
                          onChange={handleContractorChange}
                          sx={textFieldSx}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="notes"
                          label="Notes"
                          value={contractor.notes}
                          onChange={handleContractorChange}
                          multiline
                          rows={2}
                          sx={textFieldSx}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </Grid>
            </Grid>
          </Card>
        );

      case 2:
        return (
          <Card sx={sectionCardSx}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <CheckCircleOutlineIcon sx={{ color: "#2f63ad" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
                Review & Acknowledge
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: "#6b778c", mb: 2.5 }}>
              Confirm the request details and acknowledge the certification before submitting.
            </Typography>

            <Divider sx={{ mb: 2.5, borderColor: "rgba(47,99,173,0.08)" }} />

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  Type
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#223142" }}>
                  {requestTypes.find((t) => t.id === formData.request_type)?.type || "—"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  Subtype
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#223142" }}>
                  {subtypes.find((s) => s.subtype_id === formData.subtype_id)?.subtype_name || "—"}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  Description
                </Typography>
                <Typography variant="subtitle1" sx={{ color: "#223142" }}>
                  {formData.description || "—"}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  Files
                </Typography>
                <Typography variant="subtitle1" sx={{ color: "#223142" }}>
                  {selectedFiles.map((f) => f.name).join(", ") || "None"}
                </Typography>
              </Grid>
            </Grid>

            <Box
              sx={{
                maxHeight: 220,
                overflowY: "auto",
                p: 2,
                mt: 3,
                border: "1px solid rgba(47,99,173,0.08)",
                borderRadius: "14px",
                backgroundColor: "rgba(47,99,173,0.03)",
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: "pre-line", color: "#6b778c" }}>
                By submitting this application, I certify that all proposed exterior changes
                will be constructed in full compliance with the Heritage Springs Community
                Design Review Board standards and guidelines, as well as all representations,
                plans, and specifications submitted with this application. I acknowledge that
                I am responsible for the accuracy of all information provided and that any
                deviation from the approved plans may result in a requirement to modify or
                remove non-compliant work.
              </Typography>
            </Box>

            <FormControlLabel
              sx={{ mt: 1.5 }}
              control={
                <Checkbox
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />
              }
              label="I have read and agree to the above acknowledgment statement."
            />
          </Card>
        );

      default:
        return null;
    }
  };

  const nextDisabled =
    isSubmitting ||
    (activeStep === 0 && !formData.request_type) ||
    (activeStep === 1 && !formData.description.trim());

  return (
    <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
      <Box
        sx={{
          px: { xs: 1.5, sm: 2, md: 3 },
          pt: 2.25,
          pb: 1.5,
          borderBottom: "1px solid rgba(47,99,173,0.08)",
          backgroundColor: "rgba(47,99,173,0.02)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <ArchitectureIcon sx={{ color: "#2f63ad" }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
            Add Architectural Request
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
          Create a new architectural request and upload the required supporting documentation.
        </Typography>

        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{
            "& .MuiStepLabel-label": {
              fontSize: "0.9rem",
            },
            "& .MuiStepLabel-label.Mui-active": {
              fontWeight: 700,
              color: "#223142",
            },
            "& .MuiStepLabel-label.Mui-completed": {
              fontWeight: 600,
            },
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        {renderStepContent()}

        <Divider sx={{ my: 3, borderColor: "rgba(47,99,173,0.08)" }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={prevStep}
            disabled={activeStep === 0 || isSubmitting}
            sx={secondaryButtonSx}
          >
            Back
          </Button>

          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={nextStep}
              disabled={nextDisabled}
              sx={primaryButtonSx}
            >
              Next
            </Button>
          ) : (
            <Tooltip
              title={
                !acknowledged
                  ? "You must check the acknowledgment box before submitting"
                  : ""
              }
            >
              <span>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={!acknowledged || isSubmitting}
                  sx={primaryButtonSx}
                >
                  {isSubmitting ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    "Submit Request"
                  )}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 3 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default AddArchitecturalRequestSmart;
